/** @jsxImportSource @emotion/react */

import { useEffect, useState } from "react";
import {
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BaseButton from "components/common/Button/Base";
import {
  sendOtherTaxPaymentNoticeKakao,
  sendPaymentNoticeKakao
} from "pages/remotes";
import { OtherTaxType } from "models/paymentNotice.model";
import { PALETTE } from "styles/base/variables";
import { runWithConcurrency } from "utils/concurrency";

// 업로드 다이얼로그와 동일하게, 동시 발송은 최대 SEND_BATCH_SIZE건으로 제한한다.
const SEND_BATCH_SIZE = 4;

type SendStatus = "pending" | "sending" | "success" | "error";

interface SendItem {
  id: string;
  name: string;
  status: SendStatus;
  errorMessage?: string;
}

interface KakaoSendTarget {
  id: string;
  name: string;
}

interface KakaoSendDialogProps {
  isOpen: boolean;
  businesses: KakaoSendTarget[];
  period: string;
  // 지정하면 부가세/종소세/법인세 알림톡(§2.13)을, 없으면 원천세 알림톡(§2.7)을 발송한다.
  taxType?: OtherTaxType;
  onClose: () => void;
  onFinished: () => void;
}

function KakaoSendDialog({
  isOpen,
  businesses,
  period,
  taxType,
  onClose,
  onFinished
}: KakaoSendDialogProps) {
  const [items, setItems] = useState<SendItem[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    const initial: SendItem[] = businesses.map((business) => ({
      id: business.id,
      name: business.name,
      status: "pending"
    }));
    setItems(initial);
    setIsSending(true);
    setCompletedCount(0);

    let cancelled = false;

    const sendOne = async (target: SendItem) => {
      setItems((prev) =>
        prev.map((it) =>
          it.id === target.id ? { ...it, status: "sending" } : it
        )
      );

      try {
        if (taxType)
          await sendOtherTaxPaymentNoticeKakao(target.id, period, taxType);
        else await sendPaymentNoticeKakao(target.id, period);
        if (cancelled) return;
        setItems((prev) =>
          prev.map((it) =>
            it.id === target.id ? { ...it, status: "success" } : it
          )
        );
      } catch (err: any) {
        if (cancelled) return;
        const errorMessage =
          err.response?.data?.message || "카카오톡 발송 중 오류가 발생했습니다.";
        setItems((prev) =>
          prev.map((it) =>
            it.id === target.id ? { ...it, status: "error", errorMessage } : it
          )
        );
      } finally {
        if (!cancelled) setCompletedCount((prev) => prev + 1);
      }
    };

    runWithConcurrency(initial, SEND_BATCH_SIZE, sendOne, () => cancelled).then(
      () => {
        if (cancelled) return;
        setIsSending(false);
        onFinished();
      }
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const totalCount = items.length;
  const progressPercent =
    totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const successCount = items.filter((it) => it.status === "success").length;
  const errorCount = items.filter((it) => it.status === "error").length;

  const renderStatusIcon = (status: SendStatus) => {
    switch (status) {
      case "success":
        return (
          <CheckCircleIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.progressCompleted};
            `}
          />
        );
      case "error":
        return (
          <ErrorIcon
            fontSize="small"
            css={css`
              color: ${PALETTE.error};
            `}
          />
        );
      case "sending":
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.primary};
            `}
          >
            발송 중
          </span>
        );
      default:
        return (
          <span
            css={css`
              font-size: 12px;
              color: ${PALETTE.textMid};
            `}
          >
            대기
          </span>
        );
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!isSending) onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>카카오톡 발송 ({totalCount}건)</DialogTitle>
      <DialogContent>
        <div
          css={css`
            min-width: 400px;
            display: flex;
            flex-direction: column;
            gap: 16px;
          `}
        >
          {!isSending && (
            <div
              css={css`
                font-size: 13px;
                color: ${PALETTE.textMid};
              `}
            >
              발송 완료 {successCount}건
              {errorCount > 0 && ` · 발송 실패 ${errorCount}건`}
            </div>
          )}

          <div
            css={css`
              max-height: 320px;
              overflow-y: auto;
              border: 1px solid ${PALETTE.border};
              border-radius: 8px;
            `}
          >
            {items.map((item) => (
              <div
                key={item.id}
                css={css`
                  padding: 10px 14px;
                  display: flex;
                  align-items: flex-start;
                  gap: 10px;
                  border-bottom: 1px solid ${PALETTE.borderLight};

                  &:last-child {
                    border-bottom: none;
                  }
                `}
              >
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                  `}
                >
                  <div
                    css={css`
                      font-size: 13px;
                      color: ${PALETTE.text};
                      overflow: hidden;
                      text-overflow: ellipsis;
                      white-space: nowrap;
                    `}
                  >
                    {item.name}
                  </div>
                  {item.status === "error" && item.errorMessage && (
                    <div
                      css={css`
                        font-size: 12px;
                        color: ${PALETTE.error};
                      `}
                    >
                      {item.errorMessage}
                    </div>
                  )}
                </div>
                {renderStatusIcon(item.status)}
              </div>
            ))}
          </div>

          {isSending && (
            <div>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{
                  backgroundColor: PALETTE.progressBackground,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: PALETTE.progressCompleted
                  }
                }}
              />
              <div
                css={css`
                  margin-top: 4px;
                  text-align: right;
                  font-size: 12px;
                  color: ${PALETTE.textMid};
                `}
              >
                {completedCount}/{totalCount}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <div
        css={css`
          margin: 6px 24px;
          border-top: 1px solid ${PALETTE.border};
        `}
      />
      <DialogActions
        css={css`
          padding: 12px 24px 20px 24px;
        `}
      >
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          disabled={isSending}
          onClick={onClose}
        >
          닫기
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
}

export default KakaoSendDialog;
